import { v4 as uuidv4 } from "uuid";
import { BasicItem } from "@dndModels/item/BasicItem";
import { AbilityScoreType, isStatType } from "@dndModels/Types";

/**
 * Something you can do with the tool when you take the Utilize action.
 * ex: Identify a substance (DC 15)
 */
export class ArtisanToolUtilize {
  true_id = "";
  description = "";
  dc = 10;

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.description = obj.description ? obj.description : "";
      this.dc = obj.dc !== undefined ? +obj.dc : 10;
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      description: this.description,
      dc: this.dc,
    };
  }

  clone(): ArtisanToolUtilize {
    return new ArtisanToolUtilize(this.toDBObj());
  }
}

/**
 * Something the tool can craft.  Either one specific item (item_id),
 * or a group of items (item_type + category) with some exceptions (group = true).
 * ex: Antitoxin, or Any Melee weapon (except Club, Greatclub, Quarterstaff, and Whip)
 */
export class ArtisanToolCraftable {
  true_id = "";
  group = false;
  item_id = ""; // The specific item when it isn't a group
  name = ""; // How a group is displayed, ex: "Any Melee weapon"
  item_type = ""; // For groups, ex: "Weapon", "Armor"
  category = ""; // For groups, ex: "Melee", "Ranged", "Medium", "Heavy"
  except_item_ids: string[] = [];

  constructor(obj?: any) {
    this.true_id = obj && obj.true_id ? obj.true_id : uuidv4().toString();
    if (obj) {
      this.group = obj.group ? obj.group : false;
      this.item_id = obj.item_id ? obj.item_id : "";
      this.name = obj.name ? obj.name : "";
      this.item_type = obj.item_type ? obj.item_type : "";
      this.category = obj.category ? obj.category : "";
      this.except_item_ids = obj.except_item_ids ? [...obj.except_item_ids] : [];
    }
  }

  toDBObj(): any {
    return {
      true_id: this.true_id,
      group: this.group,
      item_id: this.item_id,
      name: this.name,
      item_type: this.item_type,
      category: this.category,
      except_item_ids: this.except_item_ids,
    };
  }

  clone(): ArtisanToolCraftable {
    return new ArtisanToolCraftable(this.toDBObj());
  }
}

export class ArtisanTool extends BasicItem {
  ability: AbilityScoreType = "INT"; // The ability used for checks with the tool
  utilize: ArtisanToolUtilize[] = [];
  craftables: ArtisanToolCraftable[] = [];

  constructor(obj?: any) {
    super(obj);
    this.item_type = "Artisan's Tools";
    if (obj) {
      this.ability = obj.ability && isStatType(obj.ability) ? obj.ability : "INT";
      this.utilize = obj.utilize ? obj.utilize.map((o: any) => new ArtisanToolUtilize(o)) : [];
      this.craftables = obj.craftables ? obj.craftables.map((o: any) => new ArtisanToolCraftable(o)) : [];
    }
  }

  toDBObj(): any {
    const base_db_obj = super.toDBObj();
    return {
      ...base_db_obj,
      ability: this.ability,
      utilize: this.utilize.map(o => o.toDBObj()),
      craftables: this.craftables.map(o => o.toDBObj()),
    };
  }

  toLight(): any {
    const base_db_obj = super.toLight();
    return {
      ...base_db_obj,
      ability: this.ability,
      utilize: this.utilize.map(o => o.toDBObj()),
      craftables: this.craftables.map(o => o.toDBObj()),
    };
  }

  clone(drop_ids = false): ArtisanTool {
    const new_obj = new ArtisanTool();
    new_obj.copy(this, drop_ids);
    return new_obj;
  }

  copy(copyMe: ArtisanTool, drop_ids = false): void {
    super.copy(copyMe, drop_ids);
    this.ability = copyMe.ability;
    this.utilize = copyMe.utilize.map(o => o.clone());
    this.craftables = copyMe.craftables.map(o => o.clone());
  }
}
